import React, { useEffect, useState } from 'react';
import {Table, Input, Space, Tag, Select, Empty, Button, Checkbox, Typography} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import axios from 'axios';
import { TaskData } from '../../../../types/TaskData';
import './MeasureOrderTable.css';
import { FilePdfOutlined } from "@ant-design/icons";
import { StatusUtils } from "../../../../utils/StatusUtils";
import {getConfig} from "../../../../config";
import LoadingModal from "../../../../components/loadingModal/LoadingModal";
import {useProjectContext} from "../../../../context/ProjectContext";

const { Option } = Select;
const { Text } = Typography;
const { API_BASE_URL } = getConfig();

interface MeasureOrderTableProps {
    onSelectRows: (rows: TaskData[]) => void;
    refreshKey: number;
}

const MeasureOrderTable: React.FC<MeasureOrderTableProps> = ({ onSelectRows, refreshKey }) => {
    const [data, setData] = useState<TaskData[]>([]);
    const [originalData, setOriginalData] = useState<TaskData[]>([]); // To store unfiltered data
    const { selectedProject, selectedManagerNo } = useProjectContext();
    const [selectedRows, setSelectedRows] = useState<TaskData[]>([]);
    const [globalSearch, setGlobalSearch] = useState('');
    const [showAllData, setShowAllData] = useState(false);
    const [arraysEqual, setArraysEqual] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');

    useEffect(() => {
        if (selectedProject)    {
            fetchData(selectedProject);
        }
    }, [selectedProject, refreshKey]);

    const fetchData = async (projectNo: string) => {
        try {
            const response = await axios.get<TaskData[]>(
                `${API_BASE_URL}/getReadyToDoTasks?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}&type=measure&managerNo=${selectedManagerNo}`
            );
            setOriginalData(response.data);
            setData(response.data.filter(item => item.activityStatus === 'READY')); // Default filter
            setArraysEqual(
                JSON.stringify(originalData) === JSON.stringify(data)
            );
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        setSelectedRows([]);
    };


    const getSelectableCriteria = (): { jobCodeDesc: string | null; korrex: string | null } | null => {
        if (selectedRows.length === 0) return null;
        return {
            jobCodeDesc: selectedRows[0].jobCodeDesc,
            korrex: selectedRows[0].korrex,
        };
    };

    const isRowSelectable = (record: TaskData): boolean => {
        const criteria = getSelectableCriteria();
        if (!criteria) return true; // Allow selection if no row is selected yet
        return (
            record.jobCodeDesc === criteria.jobCodeDesc &&
            record.korrex === criteria.korrex
        );
    };

    const arraysAreEqual = (arr1: any[], arr2: any[]) => {
        if (arr1.length !== arr2.length) return false;
        return arr1.every((item, index) => JSON.stringify(item) === JSON.stringify(arr2[index]));
    };

    const fetchPDFAndOpen = async (orderCardId: string) => {
        try {
            setLoadingText("PDF wird geladen...");
            setLoading(true)
            const projectId = parseInt(orderCardId.slice(0, 6), 10);
            const supplement = parseInt(orderCardId.slice(6, 8), 10);
            const protocol = parseInt(orderCardId.slice(8, 10), 10);

            const response = await axios.get(
                `${API_BASE_URL}/getOrderCardByID`, {
                    params: {
                        auth: "VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU",
                        projectId,
                        supplement,
                        protocol
                    }
                }
            );

            const pdfBase64 = response.data.backendMessage.split(',')[1];
            const pdfWindow = window.open('', '_blank');
            pdfWindow?.document.write(
                `<iframe width="100%" height="100%" src="data:application/pdf;base64,${pdfBase64}"></iframe>`
            );
        } catch (error) {
            console.error('Error fetching PDF:', error);
        } finally   {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (e: CheckboxChangeEvent) => {
        setShowAllData(e.target.checked);
        setData(e.target.checked ? originalData : originalData.filter(item => item.activityStatus === 'READY'));
    };

    const filteredData = data.filter(item =>
        item.orderCardId.toLowerCase().includes(globalSearch.toLowerCase()) ||
        item.jobCodeDesc?.toLowerCase().includes(globalSearch.toLowerCase()) ||
        item.oglDesc?.toLowerCase().includes(globalSearch.toLowerCase()) ||
        item.faz.toString().includes(globalSearch)
    );

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: TaskData[]) => {
            setSelectedRows(selectedRows);
            onSelectRows(selectedRows);
        },
        getCheckboxProps: (record: TaskData) => ({
            disabled: !isRowSelectable(record), // Disable rows that don't match jobCodeDesc and korrex
        }),
        selectedRowKeys: selectedRows.map(row => row.id),
    };

    const handleRowClick = (record: TaskData) => {
        if (record.activityStatus !== 'READY') {
            return;
        }
        const isSelected = selectedRows.some(row => row.id === record.id);
        const newSelectedRows = isSelected
            ? selectedRows.filter(row => row.id !== record.id)
            : [...selectedRows, record];

        setSelectedRows(newSelectedRows);
        onSelectRows(newSelectedRows);
    };

    const columns = [
        {
            title: 'Auftragskarte',
            dataIndex: 'orderCardId',
            key: 'orderCardId',
            sorter: (a: { orderCardId: string }, b: { orderCardId: string }) => a.orderCardId.localeCompare(b.orderCardId),
            render: (orderCardId: string) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button
                        icon={<FilePdfOutlined />}
                        onClick={(event) => {
                            event.stopPropagation();
                            fetchPDFAndOpen(orderCardId);
                        }}
                        type="primary"
                        size="small"
                    >
                        {orderCardId.replace(/(\d{6})(\d{2})(\d{2})/, '$1/$2/$3')}
                    </Button>
                </div>
            ),
        },
        {
            title: 'Produkt',
            dataIndex: 'jobCodeDesc',
            key: 'jobCodeDesc',
            sorter: (a: TaskData, b: TaskData) => (a.jobCodeDesc ?? '').localeCompare(b.jobCodeDesc ?? '')
        },
        {
            title: 'Bauabschnitt',
            dataIndex: 'oglDesc',
            key: 'oglDesc',
            sorter: (a: TaskData, b: TaskData) => (a.oglDesc ?? '').localeCompare(b.oglDesc ?? '')
        },
        {
            title: 'Protokoll-Bez.',
            dataIndex: 'protocolDesc',
            key: 'protocolDesc',
            sorter: (a: TaskData, b: TaskData) => {
                const valueA = a.protocolDesc?.substring(6) ?? '';
                const valueB = b.protocolDesc?.substring(6) ?? '';
                return valueA.localeCompare(valueB);
            },
            render: (text: string) => {
                return text?.substring(6) ?? '';
            },
            width: 250,
        },
        {
            title: 'Start',
            dataIndex: 'faz',
            key: 'faz',
            sorter: (a: TaskData, b: TaskData) => a.faz.localeCompare(b.faz),
            render: (faz: string) => {
                const [year, month, day] = faz.split('-');
                return `${day}.${month}.${year}`;
            },
        },
        {
            title: 'Status',
            dataIndex: 'activityStatus',
            key: 'activityStatus',
            sorter: (a: TaskData, b: TaskData) => (a.activityStatus ?? '').localeCompare(b.activityStatus ?? ''),
            render: (status: 'DONE' | 'READY' | 'NOT_READY' | 'PENDING') => {
                const color = StatusUtils.getColor(status);
                const translatedStatus = StatusUtils.getTranslatedStatus(status);
                return <Tag color={color}>{translatedStatus}</Tag>;
            },
        }
    ];

    return (
        <div style={{ padding: '20px' }}>
            <LoadingModal
                open={loading}
                text={loadingText}
            />
            <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                        onChange={handleCheckboxChange}
                        checked={showAllData}
                        disabled={!arraysEqual}
                        style={{ marginRight: '8px' }}
                    >
                        Alle Daten für 'Messbeauftragung' anzeigen
                    </Checkbox>
                </div>
            </Space>

            <Input
                placeholder="Suche in Auftragskarte, Produkt, Bauabschnitt oder Start"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                style={{ marginBottom: 16, width: '100%' }}
            />

            <Table
                rowSelection={{
                    type: 'checkbox',
                    ...rowSelection,
                }}
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                pagination={{ pageSize: 100 }}
                scroll={{ y: 700 }}
                locale={{
                    emptyText: <Empty description="Keine Auftragskarten zur Beauftragung vorhanden" />
                }}
                rowClassName={(record) =>
                    isRowSelectable(record)
                        ? selectedRows.includes(record)
                            ? 'selected-row'
                            : ''
                        : 'disabled-row'
                }
                onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                })}
            />
            {selectedRows.length > 1 && (
                <Text type="secondary" style={{ marginTop: 16 }}>
                    Es werden {selectedRows.length} Auftragskarten zusammengefasst
                </Text>
            )}
        </div>
    );
};

export default MeasureOrderTable;
